import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import dayjs from 'dayjs';
import { cloneDeep, compact, flatten, flattenDeep, isUndefined, uniq } from 'lodash-es';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { IconComponent } from '@shared/components/icon/icon.component';
import { CMC_EXCLUDED_URLS, ROUTING_PATH } from '@shared/constants/routingPath';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { VarDirective } from '@shared/directives/var.directive';
import { Flags, LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { MainQuery } from '@shared/store/main/main.query';
import { TrialsQuery } from '@shared/store/trials/trials.query';

import { NavigationMenuCost, NavigationMenuItem } from './constants/navigation-menu.const';
import { NavigationMenuService } from './services/navigation-menu.service';

@Component({
  animations: [
    trigger('slideVertical', [
      state(
        '*',
        style({
          height: 0,
        })
      ),
      state(
        'show',
        style({
          height: '*',
        })
      ),
      transition('* => *', [animate('400ms cubic-bezier(0.25, 0.8, 0.25, 1)')]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, IconComponent, RouterLink, TooltipDirective, VarDirective],
  providers: [NavigationMenuService],
  selector: 'aux-navigation-menu',
  styles: [
    `
      .sub-level:last-child::before {
        content: '';
        position: absolute;
        background-color: white;
        width: 2px;
        height: 50%;
        bottom: 0;
        left: -2px;
      }
    `,
  ],
  templateUrl: './navigation-menu.component.html',
})
export class NavigationMenuComponent {
  private activatedRoute = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);
  private launchDarklyService = inject(LaunchDarklyService);
  private mainQuery = inject(MainQuery);
  private navigationMenuService = inject(NavigationMenuService);
  private router = inject(Router);
  private trialsQuery = inject(TrialsQuery);

  numberOfChangeOrdersInPendingApproval$ = this.mainQuery.select('numberOfCOsInPendingApproval');

  dataSource$ = combineLatest([
    this.launchDarklyService.flags$,
    this.numberOfChangeOrdersInPendingApproval$,
    this.mainQuery.select('currentOpenMonth'),
    this.mainQuery.select('trialEndDate'),
  ]).pipe(
    distinctUntilChanged(
      (
        [oldFlags, oldCount, oldOpenPeriod, oldEndDate],
        [newFlags, newCount, newOpenPeriod, newEndDate]
      ) => {
        const usedFlags = this.getRouteFeatureFlagList(this.getRouterConfig());

        return (
          flatten(usedFlags).every((flag) => oldFlags[flag] === newFlags[flag]) &&
          oldCount === newCount &&
          oldOpenPeriod === newOpenPeriod &&
          oldEndDate === newEndDate
        );
      }
    ),
    map(([flags, badgeCount, currentOpenMonth, trialEndDate]) => {
      const isCurrentTrialCMC = this.trialsQuery.isCurrentTrialCMC(
        this.mainQuery.mainState().trialKey
      )();
      const configWithFlags = this.filterRouterConfigByFlagsAndTrialType(
        this.getRouterConfig(),
        flags,
        isCurrentTrialCMC
      );

      this.setCurrentPeriodToPeriodCloseRouter(configWithFlags, currentOpenMonth, trialEndDate);

      const changeOrderConfig = configWithFlags
        .find((x) => x.url === `/${ROUTING_PATH.BUDGET.INDEX}`)
        ?.subRoutes.find(
          (x) => x.url === `/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.CHANGE_ORDER.INDEX}`
        );

      if (changeOrderConfig && changeOrderConfig.badge) {
        changeOrderConfig.badge.amount.next(badgeCount);
      }

      return configWithFlags;
    })
  );

  expanded: Record<string, boolean> = {};
  @ViewChild('flyOutMenu') flyOutMenu!: ElementRef;
  flyOutMenuClass: string = 'top-5';
  isFlyOutMenuOpened: boolean = false;

  private THIRD_LEVEL_ROUTE = 3;

  constructor() {
    this.router.events.pipe(takeUntilDestroyed()).subscribe((routeEvent) => {
      if (routeEvent instanceof NavigationEnd) {
        this.expandParentOfCurrentUrlPage();
        this.cdr.markForCheck();
      }
    });

    this.mainQuery
      .select('trialKey')
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.expanded = {};
        this.expandParentOfCurrentUrlPage();
      });

    this.navigationMenuService
      .getNumberOfCOsInPendingApproval()
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  expandParentOfCurrentUrlPage() {
    if (this.selectedParentRoute) {
      this.expanded = {};
      this.expanded[this.selectedParentRoute] = true;
    }
  }

  filterRouterConfigByFlagsAndTrialType(
    config: NavigationMenuItem[],
    flags: Flags,
    isTrialTypeCmc: boolean
  ): NavigationMenuItem[] {
    return config.filter((item) => {
      if (isTrialTypeCmc && CMC_EXCLUDED_URLS.includes(item.url)) {
        return false;
      }

      // Hide items marked as hiddenFromNavigation
      if (item.hiddenFromNavigation) {
        return false;
      }

      if (
        ['flagKey', 'flagKeys', 'flyOutMenu'].every(
          (key) => !Object.prototype.hasOwnProperty.call(item, key)
        )
      ) {
        return true;
      }

      const flagIsOn = isUndefined(item.flagKey) || flags[item.flagKey];
      const someOfRelatedFlagsIsOn =
        isUndefined(item.flagKeys) || item.flagKeys.some((flagKey) => flags[flagKey]);

      if (flagIsOn && someOfRelatedFlagsIsOn) {
        if (item.subRoutes.length) {
          item.subRoutes = this.filterRouterConfigByFlagsAndTrialType(
            item.subRoutes || [],
            flags,
            isTrialTypeCmc
          );
        }

        if (item.flyOutMenu?.length) {
          item.flyOutMenu = this.filterRouterConfigByFlagsAndTrialType(
            item.flyOutMenu || [],
            flags,
            isTrialTypeCmc
          );
        }

        return true;
      }

      return false;
    });
  }

  getRouteFeatureFlagList(routerConfig: NavigationMenuItem[]): (keyof Flags)[] {
    return uniq(
      compact(
        flattenDeep(
          routerConfig.map((navNode) => {
            const relatedFlagKeys = navNode.flagKeys || [];
            const deepFlagKeys = (['flyOutMenu', 'subRoutes'] as const).reduce(
              (flags, propKey) => [
                ...flags,
                ...(navNode[propKey]?.length
                  ? this.getRouteFeatureFlagList(navNode[propKey] as NavigationMenuItem[])
                  : []),
              ],
              [] as (keyof Flags)[]
            );

            return [navNode.flagKey, ...relatedFlagKeys, ...deepFlagKeys];
          })
        )
      )
    );
  }

  getRouterConfig() {
    return cloneDeep(NavigationMenuCost.ROUTER_CONFIG);
  }

  private getTwoLevelRoutePath(route: string) {
    const subRoutes = route.match(/\/[a-z | (\-)]*/g);

    if (!subRoutes?.length) return;

    if (subRoutes?.length >= this.THIRD_LEVEL_ROUTE) {
      return `${subRoutes[0]}${subRoutes[1]}`;
    }

    return route.replace(/\?trial=.*/, '');
  }

  hasChild = (node: NavigationMenuItem): boolean => {
    return !!node.subRoutes && node.subRoutes.length > 0;
  };

  isActiveLink(routerLink: string) {
    const parentRoute = this.activatedRoute.snapshot?.firstChild?.url[0].path;

    const isActiveIndexRoute = parentRoute === routerLink.replace('/', '');

    if (isActiveIndexRoute) {
      return true;
    }

    const parsedUrl = this.getTwoLevelRoutePath(routerLink);
    const parsedCurrentRoute = this.getTwoLevelRoutePath(this.router.url);

    return parsedCurrentRoute === parsedUrl;
  }

  isCollapsable = (node: NavigationMenuItem): boolean =>
    !node.singleRoute && node.subRoutes.length === 0 && !node.divider;

  isDivider = (node: NavigationMenuItem): boolean => !!node.divider;

  onFlyOutMenuItemSelected(event: MouseEvent, url: string) {
    event.stopPropagation();
    this.router.navigate([url]).catch(console.error);
  }

  onItemSelected(item: NavigationMenuItem) {
    if (
      item.subRoutes &&
      item.subRoutes.length &&
      (!this.expanded[item.url] || item.url !== this.selectedParentRoute)
    ) {
      this.expanded[item.url] = !this.expanded[item.url];
    }
  }

  openFlyOutMenu() {
    this.isFlyOutMenuOpened = true;

    if (this.flyOutMenu) {
      const rect = this.flyOutMenu.nativeElement.getBoundingClientRect();

      this.flyOutMenuClass = rect.bottom > window.innerHeight ? 'bottom-5' : 'top-5';
    }
  }

  get selectedParentRoute(): string | undefined {
    const currentParentRoute = this.activatedRoute.snapshot?.firstChild?.url[0].path || '';

    const selectedParentRoute = this.getRouterConfig().find(({ url }) => {
      return url.replace('/', '') === currentParentRoute;
    })?.url;

    return selectedParentRoute;
  }

  private setCurrentPeriodToPeriodCloseRouter(
    configWithFlags: NavigationMenuItem[],
    currentOpenMonth: string,
    trialEndDate: string
  ) {
    const periodCloseRouterIndex = configWithFlags.findIndex(({ name }) => name === 'Period Close');

    if (periodCloseRouterIndex === -1 || !currentOpenMonth || !trialEndDate) return;

    const openMonthObj = dayjs(currentOpenMonth);

    // compare month and year
    if (openMonthObj.isAfter(trialEndDate, 'month')) return;

    configWithFlags[periodCloseRouterIndex].primaryBadgeInfo = openMonthObj.format('MMM YYYY');
  }

  singleRoute = (node: NavigationMenuItem): boolean => {
    return !!node?.singleRoute;
  };
}
